import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { getCurrentUser } from '../auth';

interface NavigationProps {
  onLogout: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({ onLogout }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const currentUser = getCurrentUser();
  const role = currentUser?.role;

  const buildMenuItems = () => {
    const items = [{ label: t('leads'), path: '/leads' }];

    if (role === 'SALES') {
      items.push({ label: 'Финансы', path: '/finance' });
      items.push({ label: 'Споры', path: '/disputs' });
      return items;
    }

    if (role === 'MANAGER') {
      return items;
    }

    items.push(
      { label: t('projects'), path: '/projects' },
      { label: 'Сводка проектов', path: '/projects/summary' },
      { label: t('users'), path: '/users' },
      { label: t('analytics'), path: '/analytics' },
      { label: 'Контроль качества', path: '/quality-control' },
      { label: 'Контроль качества 2', path: '/quality-control-v2' },
      { label: 'Эффективность контактов', path: '/contacts-efficiency' },
      { label: 'Интеграции', path: '/integrations' },
      { label: 'Финансы', path: '/finance' },
      { label: 'Отчёт операторы', path: '/operators-report' },
      { label: 'Матрица оператор/проект', path: '/operator-project-matrix' },
      { label: 'Часы операторов', path: '/operator-hours' },
      { label: 'Роли сотрудников', path: '/employee-roles' },
      { label: 'Google Sheets', path: '/google-sheets' },
    );
    return items;
  };

  const menuItems = buildMenuItems();

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ p: 2, backgroundColor: '#1976d2', color: 'white' }}>
        <Typography variant="h6">BoardTraff</Typography>
        {currentUser && (
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
            {currentUser.operatorName || currentUser.email}
          </Typography>
        )}
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => setDrawerOpen(false)}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => { setDrawerOpen(false); onLogout(); }}>
            <ListItemText primary={t('logout')} sx={{ color: 'error.main' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            BoardTraff
          </Typography>
          {currentUser && (
            <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
              {currentUser.operatorName || currentUser.email} ({currentUser.role})
            </Typography>
          )}
          <Button
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
            onClick={onLogout}
          >
            {t('logout')}
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 } }}
      >
        {drawer}
      </Drawer>
    </>
  );
};
